import React, { useMemo, useState } from "react";
import {
  CAccordion,
  CAccordionBody,
  CAccordionHeader,
  CAccordionItem,
  CAlert,
  CAutocomplete,
  CBadge,
  CBreadcrumb,
  CBreadcrumbItem,
  CButton,
  CCallout,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CDatePicker,
  CDateRangePicker,
  CFormCheck,
  CFormFeedback,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CLoadingButton,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CMultiSelect,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  COneTimePassword,
  CPasswordInput,
  CProgress,
  CProgressBar,
  CRow,
  CSmartPagination,
  CSmartTable,
  CSpinner,
  CStepper,
  CTab,
  CTabContent,
  CTabList,
  CTabPanel,
  CTabs,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
  CToast,
  CToastBody,
  CToastClose,
  CToastHeader,
  CToaster,
  CWidgetStatsA,
} from "@coreui/react-pro";
import AdminPageFrame from "../components/admin/AdminPageFrame";
import { useAppearance } from "../context/AppearanceContext";

const migratedRuntimeFiles = [
  "src/components/admin/AdminPageFrame.jsx",
  "src/components/admin/AdminShell.jsx",
  "src/components/public/PublicFooter.jsx",
  "src/components/public/PublicAuthLayout.jsx",
  "src/components/public/PublicHeader.jsx",
  "src/components/ApplicantShell.jsx",
  "src/pages/ApplicantApplicationWorkspace.js",
  "src/pages/Home.js",
  "src/pages/Leitbild.js",
  "src/pages/LoginPage.js",
  "src/pages/RegisterPage.js",
  "src/pages/ApplicantFrontdoorPage.jsx",
  "src/pages/legal/AGB.js",
  "src/pages/legal/Datenschutz.js",
  "src/pages/legal/Impressum.js",
  "src/pages/SecurityDashboard.js",
  "src/pages/stb/StbApplications.js",
  "src/pages/stb/StbAuthorityFilingEmployerWorkspace.jsx",
  "src/pages/stb/StbJobMarketplace.js",
];

const tableRows = [
  { component: "CButton", family: "Foundation", status: "runtime" },
  { component: "CCallout", family: "Guidance", status: "frontdoor / applications / security / filing / detail safety" },
  { component: "CLoadingButton", family: "Async action", status: "frontdoor / auth / applications / filing / security refresh" },
  { component: "CWidgetStatsA", family: "KPI", status: "security dashboard" },
  { component: "CDatePicker", family: "Forms", status: "application workspace / showcase" },
  { component: "CDateRangePicker", family: "Forms", status: "filing / showcase" },
  { component: "CMultiSelect", family: "Forms", status: "frontdoor / filing" },
  { component: "CAutocomplete", family: "Forms", status: "frontdoor" },
  { component: "CPasswordInput", family: "Auth", status: "login / register" },
  { component: "COneTimePassword", family: "Auth", status: "showcase readiness" },
  { component: "CStepper", family: "Guided workflow", status: "frontdoor / filing / showcase" },
  { component: "CSmartTable", family: "Data", status: "applications / filing / security / showcase / detail coupling" },
  { component: "CSmartPagination", family: "Data", status: "applications / filing / security / showcase" },
];

const smartColumns = [
  { key: "component", label: "Komponente", sorter: true, filter: true },
  { key: "family", label: "Bereich", sorter: true, filter: true },
  { key: "status", label: "Status", sorter: true, filter: true },
];

const workflowRows = [
  { name: "Applicant Quick Check", owner: "Applicant", state: "active" },
  { name: "Deadline Board", owner: "Admin", state: "tracked" },
  { name: "Employer Review", owner: "Employer", state: "ready" },
];

const employerQueueRows = [
  { candidate: "CAND-1042", status: "Interessant", filing: "Pack-Reife", updated: "27.03.2026 09:12", decision: "WT geteilt, Filing fast frei" },
  { candidate: "CAND-1188", status: "In Pruefung", filing: "Kandidatenanforderungen", updated: "27.03.2026 08:35", decision: "B1 offen, Rueckmeldung pruefen" },
  { candidate: "CAND-1260", status: "Neu", filing: "Arbeitsraum bei Bedarf", updated: "27.03.2026 07:58", decision: "Noch keine belastbare Entscheidung" },
];

const filingQueueRows = [
  { lane: "Offen", request: "Passkopie nachreichen", status: "Warten auf Rueckmeldung", due: "29.03.2026", detail: "Deadline aktiv, Kandidatenantwort fehlt" },
  { lane: "In Pruefung", request: "Sprachnachweis pruefen", status: "In Pruefung", due: "27.03.2026", detail: "Rueckmeldung liegt vor, Employer prueft" },
  { lane: "Erledigt", request: "Lebenslauf erhalten", status: "Erledigt", due: "26.03.2026", detail: "Request abgeschlossen, Verlauf bleibt lesbar" },
];

const adminQueueRows = [
  { source: "Security Event", type: "Rate Limit", target: "/api/login", status: "Beobachten", detail: "Live-Signal, aktuell kein Ban" },
  { source: "Fail2Ban", type: "Ban", target: "203.0.113.41", status: "Geblockt", detail: "Historische Sperre, Jail-Kontext pruefen" },
  { source: "Security Event", type: "Probeverkehr", target: "/api/stb", status: "Pruefen", detail: "Vergleich gegen aktuellen Live-Feed" },
];

const autocompleteOptions = [
  "Pflegefachkraft",
  "Elektroniker",
  "Koch",
  "Logistikfachkraft",
  "Hotelfachkraft",
];

const multiSelectOptions = [
  { value: "passport", label: "Reisepass" },
  { value: "diploma", label: "Abschluss" },
  { value: "language", label: "Sprachnachweis" },
  { value: "cv", label: "Lebenslauf" },
];

export default function AdminCoreUiProShowcasePage() {
  const { resolvedMode } = useAppearance();
  const [activeTab, setActiveTab] = useState("navigation");
  const [showModal, setShowModal] = useState(false);
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [toastPush, setToastPush] = useState(0);
  const [stepNumber, setStepNumber] = useState(2);
  const [smartPage, setSmartPage] = useState(3);
  const [otpValue, setOtpValue] = useState("");
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [professionSearch, setProfessionSearch] = useState(null);

  const themeValue = useMemo(() => {
    if (typeof document === "undefined") return "unknown";
    return (
      document.documentElement.getAttribute("data-coreui-theme") ||
      document.body.getAttribute("data-coreui-theme") ||
      resolvedMode ||
      "unset"
    );
  }, [resolvedMode]);

  const toast = (
    <CToast key={toastPush} visible autohide={false} color="info">
      <CToastHeader closeButton={false}>
        <span className="fw-semibold">CoreUI PRO Toast</span>
        <CToastClose onClick={() => setToastPush(0)} />
      </CToastHeader>
      <CToastBody>Diese Toast-Komponente kommt direkt aus `@coreui/react-pro`.</CToastBody>
    </CToast>
  );

  return (
    <AdminPageFrame
      title="CoreUI PRO Showcase"
      subtitle="Interne Referenzfläche für echte React-PRO-Komponenten, Theme-Sichtprüfung und Konflikte mit bestehenden Experts4Europe-Overrides."
      badge="CoreUI PRO Runtime"
      shellProps={{
        topSignal: "CoreUI PRO",
        topContext: "Runtime Showcase",
        shellLabel: "Technical Reference Surface",
        shellDescription: "Diese Seite dient nur der internen Sicht- und Laufzeitprüfung von CoreUI PRO innerhalb der bestehenden Admin-Familie.",
        modeLabel: "Admin Internal",
        liveLabel: "PRO",
      }}
      actions={
        <div className="coreui-pro-showcase-actions">
          <CButton color="primary" onClick={() => setShowModal(true)}>
            Modal öffnen
          </CButton>
          <CButton color="secondary" variant="outline" onClick={() => setShowOffcanvas(true)}>
            Offcanvas öffnen
          </CButton>
          <CButton color="info" variant="ghost" onClick={() => setToastPush((value) => value + 1)}>
            Toast anzeigen
          </CButton>
        </div>
      }
    >
      <div className="coreui-pro-showcase-grid">
        <CCard className="coreui-pro-showcase-card coreui-pro-showcase-card--full">
          <CCardHeader>CoreUI PRO Track Closed</CCardHeader>
          <CCardBody className="coreui-pro-showcase-stack">
            <CCallout color="success" className="mb-0">
              <strong>Finaler Referenzstand</strong>
              <div>CoreUI PRO ist jetzt produktiver Standard in Experts4Europe. Diese Seite dient nur noch als technische Abschlussreferenz fuer die real uebernommenen Muster auf Frontdoor, Auth, Applications, Filing und Security.</div>
            </CCallout>
            <CCallout color="info" className="mb-0">
              <strong>Bewusst readiness-only</strong>
              <div>`COneTimePassword` bleibt verifiziert, aber ohne echten OTP-/Reset-/MFA-Backend-Flow bewusst nicht produktiv aktiviert.</div>
            </CCallout>
            <CCallout color="warning" className="mb-0">
              <strong>Kein offener Ausbaupfad mehr</strong>
              <div>Nach diesem Close-out werden keine weiteren CoreUI-Ausbaubloecke mehr vorbereitet. Weitere UI-Arbeit laeuft nur noch als Bugfix, Runtime-Hardening oder gezielte Einzelkorrektur in der allgemeinen Projektroadmap.</div>
            </CCallout>
          </CCardBody>
        </CCard>

        <CCard className="coreui-pro-showcase-card">
          <CCardHeader>Foundations</CCardHeader>
          <CCardBody className="coreui-pro-showcase-stack">
            <div className="coreui-pro-showcase-row">
              <CButton color="primary">Primary</CButton>
              <CButton color="secondary" variant="outline">Outline</CButton>
              <CButton color="light" variant="ghost">Ghost</CButton>
            </div>
            <div className="coreui-pro-showcase-row">
              <CBadge color="primary">Runtime</CBadge>
              <CBadge color="success">Stable</CBadge>
              <CBadge color="warning">Override beobachten</CBadge>
            </div>
            <CAlert color="info" className="mb-0">
              CoreUI PRO läuft hier als echte React-PRO-Komponentenfläche innerhalb der bestehenden Admin-Shell.
            </CAlert>
            <div className="coreui-pro-showcase-row">
              <CProgress className="flex-grow-1">
                <CProgressBar value={68}>68%</CProgressBar>
              </CProgress>
              <CSpinner color="primary" />
            </div>
          </CCardBody>
        </CCard>

        <CCard className="coreui-pro-showcase-card">
          <CCardHeader>Navigation / Surface</CCardHeader>
          <CCardBody className="coreui-pro-showcase-stack">
            <CBreadcrumb className="mb-0">
              <CBreadcrumbItem href="/dashboard/admin">Admin</CBreadcrumbItem>
              <CBreadcrumbItem active>CoreUI PRO Showcase</CBreadcrumbItem>
            </CBreadcrumb>

            <CTabs activeItemKey={activeTab} onChange={setActiveTab}>
              <CTabList variant="tabs">
                <CTab itemKey="navigation">Tabs</CTab>
                <CTab itemKey="runtime">Runtime</CTab>
                <CTab itemKey="overrides">Overrides</CTab>
              </CTabList>
              <CTabContent>
                <CTabPanel className="pt-3" itemKey="navigation">
                  Tabs, Accordion, Modal, Offcanvas und Toast sind hier als echte PRO-Widgets sichtbar.
                </CTabPanel>
                <CTabPanel className="pt-3" itemKey="runtime">
                  Die Seite läuft auf `@coreui/react-pro`; die bestehende Shell und die Experts4Europe-Overrides bleiben absichtlich sichtbar.
                </CTabPanel>
                <CTabPanel className="pt-3" itemKey="overrides">
                  Nutzen für die Prüfung: Konflikte zwischen PRO-Basis und lokalen Override-Schichten werden direkt sichtbar.
                </CTabPanel>
              </CTabContent>
            </CTabs>

            <CAccordion>
              <CAccordionItem itemKey={1}>
                <CAccordionHeader>Accordion</CAccordionHeader>
                <CAccordionBody>Dieses Accordion kommt aus `@coreui/react-pro` und läuft innerhalb der bestehenden Admin-Familie.</CAccordionBody>
              </CAccordionItem>
              <CAccordionItem itemKey={2}>
                <CAccordionHeader>Showcase-Zweck</CAccordionHeader>
                <CAccordionBody>Keine Marketing-Demo, sondern technische Referenzfläche für Komponenten, Theme und Override-Verhalten.</CAccordionBody>
              </CAccordionItem>
            </CAccordion>
          </CCardBody>
        </CCard>

        <CCard className="coreui-pro-showcase-card">
          <CCardHeader>Forms</CCardHeader>
          <CCardBody className="coreui-pro-showcase-stack">
            <CRow className="g-3">
              <CCol md={6}>
                <CFormLabel htmlFor="showcase-input">Input</CFormLabel>
                <CFormInput id="showcase-input" defaultValue="Anna Becker" />
              </CCol>
              <CCol md={6}>
                <CFormLabel htmlFor="showcase-select">Select</CFormLabel>
                <CFormSelect id="showcase-select" defaultValue="applicant">
                  <option value="applicant">Applicant</option>
                  <option value="employer">Employer</option>
                  <option value="admin">Admin</option>
                </CFormSelect>
              </CCol>
              <CCol md={6}>
                <CFormLabel htmlFor="showcase-valid">Validation valid</CFormLabel>
                <CFormInput id="showcase-valid" defaultValue="ok@example.com" valid />
                <CFormFeedback valid>Format ist gültig.</CFormFeedback>
              </CCol>
              <CCol md={6}>
                <CFormLabel htmlFor="showcase-invalid">Validation invalid</CFormLabel>
                <CFormInput id="showcase-invalid" defaultValue="broken" invalid />
                <CFormFeedback invalid>Bitte gültige E-Mail-Adresse eingeben.</CFormFeedback>
              </CCol>
              <CCol md={6}>
                <CFormLabel>Date Picker</CFormLabel>
                <CDatePicker date="2026-03-27" locale="de-DE" />
              </CCol>
              <CCol md={6}>
                <CFormLabel>Date Range Picker</CFormLabel>
                <CDateRangePicker locale="de-DE" startDate="2026-03-24" endDate="2026-03-31" />
              </CCol>
            </CRow>

            <div className="coreui-pro-showcase-row">
              <CFormCheck id="showcase-checkbox" label="Checkbox aktiv" defaultChecked />
              <CFormCheck id="showcase-radio-a" type="radio" name="showcase-radio" label="Radio A" defaultChecked />
              <CFormCheck id="showcase-radio-b" type="radio" name="showcase-radio" label="Radio B" />
            </div>

            <div>
              <CFormLabel>Stepper</CFormLabel>
              <CStepper
                activeStepNumber={stepNumber}
                layout="horizontal"
                onStepChange={setStepNumber}
                steps={[
                  { label: "Pakete" },
                  { label: "Runtime" },
                  { label: "Showcase" },
                ]}
              />
            </div>
          </CCardBody>
        </CCard>

        <CCard className="coreui-pro-showcase-card">
          <CCardHeader>High-Leverage PRO Candidates</CCardHeader>
          <CCardBody className="coreui-pro-showcase-stack">
            <CCallout color="info">
              Diese Bausteine sind fuer Experts4Europe die staerksten unmittelbaren Hebel: bessere Suche,
              kontrollierte Filter, saubere Async-Aktionen, sichere Auth und deutlich gefuehrtere KPI-/Listenflaechen.
            </CCallout>
            <CCallout color="success">
              Produktiv eingesetzt in Block 1: Applicant Frontdoor nutzt `CAutocomplete`, `CMultiSelect`, `CCallout`, `CLoadingButton`; das SecurityDashboard nutzt `CWidgetStatsA`, `CCallout`, `CSmartPagination`; Employer Filing nutzt `CMultiSelect`, `CLoadingButton`, `CCallout` und `CDateRangePicker`.
            </CCallout>
            <CCallout color="success">
              Produktiv eingesetzt in Block 2: `CPasswordInput` sitzt jetzt in Login und Register, `CStepper` fuehrt Applicant Frontdoor und den Filing-Workspace, `CDatePicker` sitzt produktiv im Bewerbungs-Workspace fuer den fruehesten Start.
            </CCallout>
            <CCallout color="warning">
              `COneTimePassword` ist bewusst nur als Readiness-Pattern verifiziert. Im aktuellen Repo existiert kein echter produktiver OTP-, Reset-Code- oder MFA-Backend-Flow.
            </CCallout>
            <CCallout color="success">
              Produktiv eingesetzt in Block 3: `CSmartTable` und `CSmartPagination` haerten jetzt die Employer Applications Review Surface, Filing-Request-/History-Queues und die Admin-Review-Queue im SecurityDashboard.
            </CCallout>
            <CCallout color="info">
              Produktiv eingesetzt in Block 4: Row-to-Detail laeuft jetzt real in `StbApplications`, im Filing-Workspace fuer Request- und History-Queues sowie im `SecurityDashboard` fuer Live-Feed und Historie.
            </CCallout>
            <CCallout color="success">
              Produktiv eingesetzt in Block 5: Compare-/Delta-Lesbarkeit und sichere Aktionshierarchie wurden jetzt in `StbApplications`, `StbAuthorityFilingEmployerWorkspace.jsx` und `SecurityDashboard.js` weiter gehaertet.
            </CCallout>
            <CCallout color="info">
              Produktiv eingesetzt in Block 6: Evidence-Snapshots und kompakter Handoff-Kontext koppeln jetzt Evidenz, Blocker und naechsten zulaessigen Schritt auf Applications-, Filing- und Security-Detailflaechen enger zusammen.
            </CCallout>
            <CCallout color="success">
              Produktiv eingesetzt in Block 7: Primäraktionen sind jetzt enger an Evidence und Readback gekoppelt; bestaetigte Zustandswechsel und read-only Review-Disziplin werden im selben Arbeitsraum erklaert.
            </CCallout>

            <CRow className="g-3">
              <CCol md={6}>
                <CFormLabel>Applicant / Berufssuche via Autocomplete</CFormLabel>
                <CAutocomplete
                  options={autocompleteOptions}
                  placeholder="Beruf eingeben"
                  value={professionSearch?.label || professionSearch || ""}
                  onChange={setProfessionSearch}
                  cleaner
                  search
                  showHints
                />
              </CCol>
              <CCol md={6}>
                <CFormLabel>Employer / Filing-Dokumente via Multi Select</CFormLabel>
                <CMultiSelect
                  options={multiSelectOptions}
                  placeholder="Pflichtunterlagen auswaehlen"
                  selectionType="tags"
                  search
                  cleaner
                  value={selectedDocs}
                  onChange={(selected) => setSelectedDocs(selected.map((item) => item.value))}
                />
              </CCol>
              <CCol md={6}>
                <CFormLabel>Async-Aktion</CFormLabel>
                <CLoadingButton color="primary" loading disabledOnLoading>
                  Filing-Pack vorbereiten
                </CLoadingButton>
              </CCol>
              <CCol md={6}>
                <CFormLabel>Deadline-Fenster im Filing</CFormLabel>
                <CDateRangePicker locale="de-DE" startDate="2026-03-27" endDate="2026-04-04" />
              </CCol>
              <CCol md={6}>
                <CFormLabel>Security / Password Input</CFormLabel>
                <CPasswordInput value="ExampleSecret123" readOnly />
              </CCol>
              <CCol md={12}>
                <CFormLabel>One-Time Password / Admin-Verifikation</CFormLabel>
                <COneTimePassword value={otpValue} onChange={setOtpValue} />
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>

        <CCard className="coreui-pro-showcase-card">
          <CCardHeader>Mini-Surfaces</CCardHeader>
          <CCardBody className="coreui-pro-showcase-stack">
            <div className="coreui-pro-showcase-note">
              <strong>Login / Register Password Pattern</strong>
              <CPasswordInput value="ApplicantSecret123" readOnly />
            </div>

            <div className="coreui-pro-showcase-note">
              <strong>OTP / Verification Readiness</strong>
              <COneTimePassword value={otpValue} onChange={setOtpValue} />
              <small>Nur Readiness-Muster: kein produktiver OTP-Backend-Flow im aktuellen System.</small>
            </div>

            <div className="coreui-pro-showcase-note">
              <strong>Applicant Quick Check Stepper</strong>
              <CStepper
                activeStepNumber={2}
                layout="horizontal"
                steps={[
                  { label: "Ausgangslage" },
                  { label: "Beruf & Sprache" },
                  { label: "Dokumente" },
                  { label: "Ergebnis" },
                ]}
              />
            </div>

            <div className="coreui-pro-showcase-note">
              <strong>Employer Filing Stepper</strong>
              <CStepper
                activeStepNumber={3}
                layout="horizontal"
                steps={[
                  { label: "Kandidatenanforderungen" },
                  { label: "Dokumente" },
                  { label: "Pack-Reife" },
                  { label: "Verlauf" },
                ]}
              />
            </div>

            <div className="coreui-pro-showcase-note">
              <strong>Single-Date Use Case</strong>
              <CDatePicker date="2026-04-15" locale="de-DE" />
              <small>Produktiv eingesetzt fuer den fruehesten Start im Bewerbungs-Workspace.</small>
            </div>

            <div className="coreui-pro-showcase-note">
              <strong>Fristen / Deadlines</strong>
              <div className="coreui-pro-showcase-row">
                <CBadge color="warning">B1 Nachweis bis 12.04.</CBadge>
                <CBadge color="info">Anerkennung offen</CBadge>
                <CBadge color="success">Arbeitgeber-Review bereit</CBadge>
              </div>
              <CProgress>
                <CProgressBar color="warning" value={54}>54% erledigt</CProgressBar>
              </CProgress>
            </div>

            <div className="coreui-pro-showcase-note">
              <strong>Status / Workflow</strong>
              <CSmartTable
                columns={[
                  { key: "name", label: "Segment" },
                  { key: "owner", label: "Owner" },
                  { key: "state", label: "Status" },
                ]}
                items={workflowRows}
                itemsPerPage={3}
                pagination={false}
                tableProps={{ hover: true, responsive: true }}
              />
            </div>

            <div className="coreui-pro-showcase-row">
              <CWidgetStatsA title="Review offen" value="24" color="primary" />
              <CWidgetStatsA title="Deadline heute" value="6" color="warning" />
            </div>

            <div className="coreui-pro-showcase-note">
              <strong>Employer Applications Queue Pattern</strong>
              <CSmartTable
                columns={[
                  { key: "candidate", label: "Kandidat" },
                  { key: "status", label: "Status" },
                  { key: "filing", label: "Filing" },
                  { key: "updated", label: "Aktualisiert" },
                ]}
                items={employerQueueRows}
                pagination={false}
                tableProps={{ hover: true, responsive: true }}
              />
              <CCallout color="info" className="mt-3">
                <strong>Decision Detail Pattern</strong>
                <div>CAND-1188 ist aktiv: In Pruefung, Filing in Kandidatenanforderungen, B1 offen.</div>
                <div className="mt-2">Die Detailflaeche bleibt im selben Review-Arbeitsraum und fuehrt Guardrails getrennt von Aktionen.</div>
              </CCallout>
              <CCallout color="warning" className="mt-3">
                <strong>Compare + Action Safety</strong>
                <div>Status vs Blocker, letzte Aktivitaet vs naechster Blick und Fallkontext vs Anzeige werden kompakt gegengelesen.</div>
                <div className="mt-2">Primaer bleibt genau eine Aktion: Entscheidung speichern. Filing-Refresh bleibt sichtbar nachrangig.</div>
              </CCallout>
              <CCallout color="info" className="mt-3">
                <strong>Evidence + Handoff Pattern</strong>
                <div>Die Surface beantwortet kompakt: Wo steht der Fall, was traegt die Entscheidungsvorbereitung, was blockiert und was ist der naechste zulaessige Schritt.</div>
              </CCallout>
              <CCallout color="success" className="mt-3">
                <strong>Save Confirmation Pattern</strong>
                <div>Vor dem Save bleibt lesbar, was gespeichert wird. Nach dem Save spiegelt dieselbe Flaeche den neuen internen Stand und den naechsten zulaessigen Schritt zurueck.</div>
              </CCallout>
              <div className="coreui-pro-showcase-row">
                <CLoadingButton color="primary" loading={false}>Entscheidung speichern</CLoadingButton>
                <CButton color="secondary" variant="outline">Filing aktualisieren</CButton>
                <CBadge color="warning">WT geteilt</CBadge>
                <CBadge color="info">Filing offen</CBadge>
              </div>
            </div>

            <div className="coreui-pro-showcase-note">
              <strong>Filing Request / History Queue Pattern</strong>
              <CSmartTable
                columns={[
                  { key: "lane", label: "Lane" },
                  { key: "request", label: "Request" },
                  { key: "status", label: "Status" },
                  { key: "due", label: "Frist" },
                ]}
                items={filingQueueRows}
                pagination={false}
                tableProps={{ hover: true, responsive: true }}
              />
              <CCallout color="warning" className="mt-3">
                <strong>Queue-to-Detail / Gate Pattern</strong>
                <div>Detail zeigt Frist, Rueckmeldestand, Filing-Relevanz und den naechsten zulaessigen Schritt, ohne die Pack-Logik umzubauen.</div>
              </CCallout>
              <CCallout color="info" className="mt-3">
                <strong>Action Hierarchy Pattern</strong>
                <div>Primaer bleibt Freeze/Refreeze. Ruecknahme-, Freigabe- und Archivaktionen bleiben sichtbar getrennt und erklaert.</div>
              </CCallout>
              <CCallout color="success" className="mt-3">
                <strong>Evidence Snapshot Pattern</strong>
                <div>Request- und History-Details verdichten jetzt Status, Frist, letzte Aktivitaet, Delta und naechsten Blickpunkt als ruhige Handoff-Flaeche.</div>
              </CCallout>
              <CCallout color="warning" className="mt-3">
                <strong>State Transition Readback Pattern</strong>
                <div>Nach einem Freeze-/Release-nahen Schritt bleibt dieselbe Flaeche aktiv und liest den neuen Zustand, den dominanten Blocker und den naechsten zulaessigen Schritt zurueck.</div>
              </CCallout>
              <div className="rounded-4 border p-3">
                <div className="fw-semibold">Version 6 · Freeze review</div>
                <div className="text-body-secondary mt-1">Compare-Kontext: 2 neue Dokumente, 1 offener Task weniger, keine harten Release-Blocker.</div>
              </div>
              <div className="coreui-pro-showcase-row">
                <CLoadingButton color="primary" loading={false}>Pack fuer Pruefung einfrieren</CLoadingButton>
                <CButton color="secondary" variant="outline">Zur Bearbeitung zurueck</CButton>
                <CButton color="danger" variant="outline" disabled>Archivieren</CButton>
              </div>
            </div>

            <div className="coreui-pro-showcase-note">
              <strong>Admin Review Queue Pattern</strong>
              <CSmartTable
                columns={[
                  { key: "source", label: "Quelle" },
                  { key: "type", label: "Typ" },
                  { key: "target", label: "Ziel" },
                  { key: "status", label: "Status" },
                ]}
                items={adminQueueRows}
                pagination={false}
                tableProps={{ hover: true, responsive: true }}
              />
              <CCallout color="info" className="mt-3">
                <strong>Security Review Detail Pattern</strong>
                <div>Live- und Verlaufseintraege bleiben getrennt, aber jede Zeile kann jetzt im selben Dashboard in eine kompakte Detailflaeche uebergehen.</div>
              </CCallout>
              <CCallout color="warning" className="mt-3">
                <strong>Action Safety Pattern</strong>
                <div>Die Detailflaeche bleibt bewusst lesend. Reale Async-Aktion ist nur der sichere Refresh des Gesamtarbeitsraums.</div>
              </CCallout>
              <CCallout color="info" className="mt-3">
                <strong>Review Evidence Pattern</strong>
                <div>Zeitpunkt, Quelle, Ziel, Kontext und naechster Pruefblick bleiben kompakt gebuendelt, ohne ein neues Security-Cockpit zu bauen.</div>
              </CCallout>
              <CCallout color="success" className="mt-3">
                <strong>Read-only Review vs. echte Action</strong>
                <div>Die einzige echte Aktion bleibt Refresh. Die Detailflaeche selbst bestaetigt nur den neuen Datenstand und bleibt lesend.</div>
              </CCallout>
              <div className="coreui-pro-showcase-row">
                <CLoadingButton color="primary" loading={false}>Security-Stand aktualisieren</CLoadingButton>
                <CBadge color="info">Detail read-only</CBadge>
              </div>
            </div>
          </CCardBody>
        </CCard>

        <CCard className="coreui-pro-showcase-card">
          <CCardHeader>Data</CCardHeader>
          <CCardBody className="coreui-pro-showcase-stack">
            <div className="coreui-pro-showcase-table-wrap">
              <CTable hover responsive>
                <CTableHead>
                  <CTableRow>
                    <CTableHeaderCell>Komponente</CTableHeaderCell>
                    <CTableHeaderCell>Bereich</CTableHeaderCell>
                    <CTableHeaderCell>Status</CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {tableRows.map((row) => (
                    <CTableRow key={row.component}>
                      <CTableDataCell>{row.component}</CTableDataCell>
                      <CTableDataCell>{row.family}</CTableDataCell>
                      <CTableDataCell>{row.status}</CTableDataCell>
                    </CTableRow>
                  ))}
                </CTableBody>
              </CTable>
            </div>

            <CSmartTable
              columns={smartColumns}
              items={tableRows}
              columnFilter
              columnSorter
              itemsPerPage={5}
              itemsPerPageSelect
              pagination
              tableProps={{ hover: true, responsive: true }}
            />

            <CSmartPagination
              pages={12}
              activePage={smartPage}
              onActivePageChange={setSmartPage}
              doubleArrows
              dots
              align="end"
            />
          </CCardBody>
        </CCard>

        <CCard className="coreui-pro-showcase-card">
          <CCardHeader>Theme / Runtime</CCardHeader>
          <CCardBody className="coreui-pro-showcase-stack">
            <div className="coreui-pro-showcase-note">
              <strong>data-coreui-theme</strong>
              <span>{themeValue}</span>
            </div>
            <div className="coreui-pro-showcase-note">
              <strong>Paketstatus</strong>
              <span>`@coreui/react-pro`, `@coreui/coreui-pro` und `@coreui/icons-pro` sind aktive Basis.</span>
              <span>`@coreui/react` und `@coreui/coreui` sind in der Endlage aus Runtime und Paketstack entfernt.</span>
              <span>Der CoreUI-PRO-Track ist fachlich und technisch abgeschlossen; diese Showcase-Seite bleibt als finale Referenzflaeche erhalten.</span>
            </div>
            <div className="coreui-pro-showcase-note">
              <strong>Bereits auf react-pro migriert</strong>
              <ul className="mb-0">
                {migratedRuntimeFiles.map((file) => (
                  <li key={file}>{file}</li>
                ))}
              </ul>
            </div>
          </CCardBody>
        </CCard>

        <CCard className="coreui-pro-showcase-card coreui-pro-showcase-card--full">
          <CCardHeader>Experts4Europe Override-Zone</CCardHeader>
          <CCardBody className="coreui-pro-showcase-stack">
            <p className="mb-0">
              Diese Zone mischt absichtlich CoreUI PRO mit bestehenden Experts4Europe-Klassen aus `admin-coreui.css`
              und `design-governance-foundation.css`, damit Konflikte sichtbar bleiben.
            </p>
            <div className="coreui-pro-showcase-override-zone">
              <div className="admin-core-topbar__panel">
                <span className="admin-core-kicker">Override sichtbar</span>
                <strong>Admin Topbar Panel</strong>
              </div>
              <div className="coreui-pro-showcase-row">
                <CBadge className="admin-core-badge">Admin Badge Override</CBadge>
                <button type="button" className="public-core-btn public-core-btn--primary">
                  Public Button Override
                </button>
                <div className="applicant-core-topbar__panel">
                  <span className="applicant-core-kicker">Applicant Override</span>
                  <strong>Panel Check</strong>
                </div>
              </div>
            </div>
          </CCardBody>
        </CCard>
      </div>

      <COffcanvas placement="end" visible={showOffcanvas} onHide={() => setShowOffcanvas(false)}>
        <COffcanvasHeader closeButton>
          <COffcanvasTitle>CoreUI PRO Offcanvas</COffcanvasTitle>
        </COffcanvasHeader>
        <COffcanvasBody>
          Diese Offcanvas-Surface dient nur der internen Laufzeitprüfung innerhalb des Showcase.
        </COffcanvasBody>
      </COffcanvas>

      <CModal visible={showModal} onClose={() => setShowModal(false)}>
        <CModalHeader>
          <CModalTitle>CoreUI PRO Modal</CModalTitle>
        </CModalHeader>
        <CModalBody>Die Modal-Komponente wird hier direkt aus `@coreui/react-pro` gerendert.</CModalBody>
        <CModalFooter>
          <CButton color="secondary" variant="outline" onClick={() => setShowModal(false)}>
            Schließen
          </CButton>
        </CModalFooter>
      </CModal>

      <CToaster placement="top-end" push={toastPush ? toast : undefined} />
    </AdminPageFrame>
  );
}
